import { Flex, type FlexProps } from "@chakra-ui/react";
import { type Theme } from "contexts/color-theme";
import React, { type PropsWithChildren } from "react";
import { getTransparentBrandColorHex } from "theme/colors";
import { remCalc } from "theme/utils";
import { CloseIcon } from "ui/icons";

export type ChipProps = PropsWithChildren<TransparentChipProps | ColorChipProps>;

type SharedChipProps = {
  /**
   * The html element to render the chip as.
   *
   * @default "span"
   */
  as?: FlexProps["as"];
};

type TransparentChipProps = SharedChipProps & {
  variant?: "transparent";
};

export type ChipColor = "gray" | "green" | "yellow" | "red" | "violet" | "blue";

type ColorChipProps = SharedChipProps & {
  /**
   * Color theme for the chip.
   *
   * @default "light"
   */
  theme?: Theme;

  /**
   * Specify one of the `ChipColor` options.
   */
  variant: ChipColor;

  /**
   * If `onCloseClick` is passed in, the `Chip` will include an interactive close icon.
   */
  onCloseClick?: () => void;
};

// This style of type checking will allow us to easily use a discriminated union w/ a default value
function isColorChipProps(props: TransparentChipProps | ColorChipProps): props is ColorChipProps {
  return Boolean(props.variant && props.variant !== "transparent");
}

/**
 * Renders out a transparent, text-only chip by default. Can optionally specify a color to render
 * a pill-style chip with a background color. Color chips can specify an `onCloseClick` handler
 * that will be used to render out an interactive close icon on the chip.
 */
export function Chip(props: ChipProps) {
  // All of these properties will be set for color bg chips, but ignored for transparent chips
  let bgColor;
  let textColor;
  let onCloseClick;
  let py;
  let px;
  let height;

  if (isColorChipProps(props)) {
    if (props.theme === "dark") {
      bgColor = getTransparentBrandColorHex(props.variant, 700, 0.6);
      textColor = `new.${props.variant}.50`;
    } else {
      bgColor = `new.${props.variant}.50`;
      textColor = props.variant === "gray" ? "brand.text.light" : `new.${props.variant}.900`;
    }

    onCloseClick = props.onCloseClick;
    py = "tiny";
    px = "smallest";
    // Use a fixed chip height to avoid any fractional pixels when using icons
    height = remCalc(36);
  }

  return (
    <Flex
      as={props.as ?? "span"}
      align="center"
      borderRadius="huger"
      bgColor={bgColor}
      color={textColor}
      fontWeight="semibold"
      height={height}
      justify="center"
      lineHeight="none"
      textStyle="caption1"
      whiteSpace="pre"
      py={py}
      px={px}
    >
      {props.children}

      {onCloseClick && <CloseIcon boxSize="icon.md" ml="tiny" opacity={0.5} cursor="pointer" onClick={onCloseClick} />}
    </Flex>
  );
}
